/**
 * Approval Inbox — Pending queue with card-based approvals
 *
 * Features:
 * - Top stats bar (pending, approved today, rejected today, avg time)
 * - Filters by type, level, priority
 * - Expandable cards with type-specific previews
 * - Approve / Edit & Approve / Reject actions
 * - SLA countdown on each card
 * - 2-person rule progress indicator
 * - Bulk approve (low-risk items only)
 * - Checkbox selection with bulk toolbar
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Search, Clock, CheckCircle, XCircle, AlertTriangle,
  ChevronDown, Wrench, Mail, Users, FileText, Shield,
  CreditCard, Bell, Zap, Timer, CheckSquare, Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { AgentIcon } from '@/components/shared/icon-picker';
import { MarkdownRenderer } from '@/components/chat/MarkdownRenderer';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  APPROVAL_TYPE_LABELS,
  APPROVAL_LEVEL_STYLES,
  APPROVAL_PRIORITY_STYLES,
  type ApprovalRequest,
  type ApprovalType,
  type ApprovalLevel,
  type ApprovalPriority,
} from '@/lib/mock-approvals-data';
import {
  usePendingApprovals,
  useApproveDecision,
  useRejectDecision,
  useApprovalStats,
  useBulkDecision,
} from '@/lib/use-agent-approvals';
import { summarizeBulkOutcome, type BulkOutcome } from '@/lib/approvals-bulk';
import type { LucideIcon } from 'lucide-react';

export const Route = createFileRoute('/approvals/')({
  component: ApprovalInboxPage,
});

// Type icons
const TYPE_ICONS: Record<ApprovalType, LucideIcon> = {
  tool_call: Wrench, email_draft: Mail, crm_response: Users, document: FileText,
  access_change: Shield, purchase_order: CreditCard, notification: Bell, custom: Zap,
};

// Time helpers
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function timeRemaining(dateStr: string): { text: string; urgent: boolean } {
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return { text: 'Expired', urgent: true };
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return { text: `${mins}m left`, urgent: mins < 15 };
  const hrs = Math.floor(mins / 60);
  return { text: `${hrs}h left`, urgent: hrs < 1 };
}

function ApprovalInboxPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Real-data: agent_tool_approvals via /agent-approvals?status=pending.
  const { data: pendingFromServer = [] } = usePendingApprovals();
  // R3 P1 #3 (fix-automate-r3-p1) — wire stats tiles to /approvals/stats.
  // Replaces the hardcoded `|| 3` / `|| 1` / `'18m'` fallbacks.
  const { data: stats, isLoading: statsLoading } = useApprovalStats();
  const approveDecision = useApproveDecision();
  const rejectDecision = useRejectDecision();
  const bulkDecision = useBulkDecision();
  const approvals = pendingFromServer;
  // Setter is unused now but kept (typed) so the rest of the page continues to compile.
  const setApprovals: (a: ApprovalRequest[]) => void = () => {};
  void setApprovals;
  const [typeFilter, setTypeFilter] = useState<'all' | ApprovalType>('all');
  const [levelFilter, setLevelFilter] = useState<'all' | ApprovalLevel>('all');
  const [priorityFilter, setPriorityFilter] = useState<'all' | ApprovalPriority>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApprovalRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Filter
  const filtered = approvals.filter(a => {
    if (a.status !== 'pending') return false;
    if (typeFilter !== 'all' && a.type !== typeFilter) return false;
    if (levelFilter !== 'all' && a.level !== levelFilter) return false;
    if (priorityFilter !== 'all' && a.priority !== priorityFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return a.action.title.toLowerCase().includes(q) || a.agent.name.toLowerCase().includes(q) || a.toolDisplayName.toLowerCase().includes(q);
  });

  // Stats — wired to /approvals/stats (today-scoped variants).
  // R3 P1 #3 (fix-automate-r3-p1): no more `|| 3` / `|| 1` fabrications.
  // Loading state renders an em-dash so the user sees "not yet known"
  // rather than a misleading zero or fake count.
  const pendingCount = stats?.pending ?? approvals.filter(a => a.status === 'pending').length;
  const approvedToday = stats?.approvedToday ?? 0;
  const rejectedToday = stats?.rejectedToday ?? 0;
  function formatDuration(ms: number): string {
    if (!ms || ms <= 0) return '—';
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = mins / 60;
    if (hrs < 24) return `${hrs.toFixed(1)}h`;
    return `${Math.round(hrs / 24)}d`;
  }
  const avgDecisionDisplay = statsLoading
    ? '…'
    : formatDuration(stats?.avgDecisionMsToday ?? 0);

  // Selection helpers
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(a => a.id)));
    }
  };

  const selectedItems = filtered.filter(a => selectedIds.has(a.id));
  const bulkApprovable = selectedItems.filter(a => a.riskScore <= 50 && a.level === 'single');
  const hasNonBulkApprovable = selectedItems.some(a => a.riskScore > 50 || a.level !== 'single');

  // Actions — wired to /agent-approvals decision endpoints.
  const handleApprove = (id: string) => {
    const item = approvals.find(a => a.id === id);
    approveDecision.mutate(
      { id },
      {
        onSuccess: () => {
          setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
          toast.success(`Approved: ${item?.action.title ?? id}`);
        },
        onError: (e) => toast.error('Approve failed', { description: e.message }),
      },
    );
  };

  // Bulk approve — fix #662 (UI cluster).
  //
  // The previous implementation caught individual mutation failures as
  // `null`, cleared the selection, closed the dialog, and toasted
  // blanket success regardless of how many actually failed. That made
  // bulk approve a trust hazard in a P1 human-in-the-loop workflow.
  //
  // New behaviour:
  //   1. `executeBulkApprovals` partitions ids into succeeded / failed
  //      (with the underlying Error preserved).
  //   2. Only the succeeded ids are removed from the selection — the
  //      failed rows stay visible / selected so the user can investigate
  //      or retry.
  //   3. The dialog only auto-closes on full success. On any failure we
  //      leave it open so the user can see the partial result before
  //      acting again.
  //   4. The toast distinguishes success / partial-failure / total-
  //      failure via `summarizeBulkOutcome` → toast.success /
  //      toast.warning / toast.error.
  const handleBulkApprove = async () => {
    const ids = bulkApprovable.map(a => a.id);
    if (ids.length === 0) return;

    let outcome: BulkOutcome;
    try {
      const resp = await bulkDecision.mutateAsync({
        approvalIds: ids,
        action: 'approve',
      });
      outcome = {
        succeeded: resp.results.filter(r => r.error === null).map(r => r.id),
        failed: resp.results
          .filter(r => r.error !== null)
          .map(r => ({
            id: r.id,
            error: new Error(r.error?.message ?? r.error?.code ?? 'Failed'),
          })),
      };
    } catch (err) {
      // The whole batch call failed (e.g. 400/network) — surface every id
      // as failed so the user gets the honest total-failure toast.
      const e = err instanceof Error ? err : new Error(String(err));
      outcome = { succeeded: [], failed: ids.map(id => ({ id, error: e })) };
    }

    // Only clear selection for items that actually succeeded.
    if (outcome.succeeded.length > 0) {
      const succeededSet = new Set(outcome.succeeded);
      setSelectedIds(prev => {
        const next = new Set<string>();
        for (const id of prev) {
          if (!succeededSet.has(id)) next.add(id);
        }
        return next;
      });
    }

    // Close the confirmation dialog only on full success.
    if (outcome.failed.length === 0) {
      setBulkConfirmOpen(false);
    }

    const summary = summarizeBulkOutcome(outcome);
    const opts = summary.description ? { description: summary.description } : undefined;
    switch (summary.kind) {
      case 'success':
        toast.success(summary.title, opts);
        break;
      case 'warning':
        toast.warning(summary.title, opts);
        break;
      case 'error':
        toast.error(summary.title, opts);
        break;
      case 'info':
        toast(summary.title, opts);
        break;
    }
  };

  const handleReject = () => {
    if (!rejectTarget) return;
    rejectDecision.mutate(
      { id: rejectTarget.id, rationale: rejectReason },
      {
        onSuccess: () => {
          setSelectedIds(prev => { const next = new Set(prev); next.delete(rejectTarget.id); return next; });
          toast.success(`Rejected: ${rejectTarget.action.title}`);
          setRejectTarget(null);
          setRejectReason('');
        },
        onError: (e) => toast.error('Reject failed', { description: e.message }),
      },
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{pendingCount}</p><p className="text-[10px] text-muted-foreground">Pending</p></CardContent></Card>
        <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{approvedToday}</p><p className="text-[10px] text-muted-foreground">Approved Today</p></CardContent></Card>
        <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-2xl font-bold text-red-600 dark:text-red-400">{rejectedToday}</p><p className="text-[10px] text-muted-foreground">Rejected Today</p></CardContent></Card>
        <Card><CardContent className="pt-3 pb-2 text-center"><p className="text-2xl font-bold">{avgDecisionDisplay}</p><p className="text-[10px] text-muted-foreground">Avg Decision Time</p></CardContent></Card>
      </div>

      {/* Filters + bulk actions */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search approvals..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={v => setTypeFilter(v as 'all' | ApprovalType)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {Object.entries(APPROVAL_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={levelFilter} onValueChange={v => setLevelFilter(v as 'all' | ApprovalLevel)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Levels" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="single">Single Confirm</SelectItem>
            <SelectItem value="supervisor">Supervisor</SelectItem>
            <SelectItem value="two_person">2-Person Rule</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={v => setPriorityFilter(v as 'all' | ApprovalPriority)}>
          <SelectTrigger className="w-28"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action toolbar — appears when items are selected */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-muted/50 border rounded-lg px-4 py-2">
          <Checkbox
            checked={selectedIds.size === filtered.length}
            onCheckedChange={selectAll}
          />
          <span className="text-xs font-medium">{selectedIds.size} selected</span>
          <Separator orientation="vertical" className="h-5" />
          {bulkApprovable.length > 0 && (
            <Button size="sm" className="gap-1.5 h-7 text-xs" onClick={() => setBulkConfirmOpen(true)}>
              <CheckCircle className="h-3.5 w-3.5" />
              Bulk Approve ({bulkApprovable.length})
            </Button>
          )}
          {hasNonBulkApprovable && (
            <span className="text-[10px] text-muted-foreground">
              {selectedItems.length - bulkApprovable.length} item{selectedItems.length - bulkApprovable.length > 1 ? 's' : ''} require{selectedItems.length - bulkApprovable.length === 1 ? 's' : ''} individual review (high risk or supervisor/2-person)
            </span>
          )}
          <Button variant="ghost" size="sm" className="h-7 text-xs ml-auto" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      )}

      {/* Select all toggle when no items selected */}
      {selectedIds.size === 0 && filtered.length > 0 && (
        <div className="flex items-center gap-2">
          <button onClick={selectAll} className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
            <Square className="h-3 w-3" /> Select all for bulk actions
          </button>
        </div>
      )}

      {/* Approval cards */}
      <div className="space-y-3">
        {filtered.map(item => {
          const isExpanded = expandedId === item.id;
          const isSelected = selectedIds.has(item.id);
          const TypeIcon = TYPE_ICONS[item.type];
          const sla = timeRemaining(item.expiresAt);
          const levelStyle = APPROVAL_LEVEL_STYLES[item.level];
          const priorityStyle = APPROVAL_PRIORITY_STYLES[item.priority];

          return (
            <Card key={item.id} className={cn('transition-colors', item.priority === 'critical' && 'border-red-500/30', isSelected && 'ring-1 ring-primary/30 bg-primary/[0.02]')}>
              <CardContent className="pt-4 pb-3">
                {/* Main row */}
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="pt-0.5 shrink-0">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(item.id)}
                    />
                  </div>

                  {/* Left: Type + Agent */}
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                      <TypeIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <AgentIcon iconName={item.agent.icon} colorName={item.agent.iconColor} size="sm" />
                  </div>

                  {/* Center: Action info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium">{item.action.title}</p>
                      <Badge variant="outline" className={cn('text-[9px]', APPROVAL_TYPE_LABELS[item.type].color)}>
                        {APPROVAL_TYPE_LABELS[item.type].label}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{item.action.description}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-[10px] text-muted-foreground">{item.agent.name}</span>
                      <span className="text-[10px] text-muted-foreground">·</span>
                      <span className="text-[10px] font-mono text-muted-foreground">{item.toolDisplayName}</span>
                      <span className="text-[10px] text-muted-foreground">·</span>
                      <span className="text-[10px] text-muted-foreground">by {item.requestedBy}</span>
                      <span className="text-[10px] text-muted-foreground">·</span>
                      <span className="text-[10px] text-muted-foreground">{timeAgo(item.requestedAt)}</span>
                    </div>
                  </div>

                  {/* Right: Priority + SLA + Level */}
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className={cn('text-[9px]', priorityStyle.color)}>{priorityStyle.label}</Badge>
                      <Badge variant="outline" className={cn('text-[9px]', levelStyle.color)}>{levelStyle.label}</Badge>
                    </div>
                    <div className={cn('flex items-center gap-1 text-[10px]', sla.urgent ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
                      <Timer className="h-3 w-3" />
                      {sla.text}
                    </div>
                    {/* Risk score */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">Risk</span>
                      <Progress value={item.riskScore} className={cn('w-12 h-1.5', item.riskScore > 70 ? '[&>div]:bg-red-500' : item.riskScore > 40 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500')} />
                      <span className="text-[10px] text-muted-foreground">{Math.round(item.riskScore)}%</span>
                    </div>
                    {/* 2-person progress */}
                    {item.level === 'two_person' && item.requiredApprovals && (
                      <Badge variant="outline" className="text-[9px]">
                        {item.approvals?.length ?? 0}/{item.requiredApprovals} approved
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Expand toggle */}
                <button className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground hover:text-foreground transition-colors ml-7" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                  <ChevronDown className={cn('h-3 w-3 transition-transform', isExpanded && 'rotate-180')} />
                  {isExpanded ? 'Hide details' : 'Show details'}
                </button>

                {/* Expanded evidence */}
                {isExpanded && (
                  <div className="mt-3 pt-3 border-t space-y-3 ml-7">
                    {/* Parameters */}
                    {item.evidence.parameters && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1.5">Parameters</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Object.entries(item.evidence.parameters).map(([key, val]) => (
                            <div key={key} className="bg-muted/30 rounded p-2">
                              <p className="text-[10px] text-muted-foreground">{key}</p>
                              <p className="text-xs font-mono">{String(val)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Preview (email body, CRM response, etc.) */}
                    {item.evidence.preview && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1.5">
                          {item.type === 'email_draft' ? 'Email Preview' : item.type === 'crm_response' ? 'Response Preview' : item.type === 'notification' ? 'Message Preview' : 'Content Preview'}
                        </p>
                        {item.evidence.subject && (
                          <p className="text-xs font-medium mb-1">Subject: {item.evidence.subject}</p>
                        )}
                        {item.evidence.recipients && (
                          <p className="text-[10px] text-muted-foreground mb-1.5">To: {item.evidence.recipients.join(', ')}</p>
                        )}
                        <div className="bg-muted/30 rounded-lg p-3 prose prose-sm dark:prose-invert max-w-none">
                          <MarkdownRenderer content={item.evidence.preview} />
                        </div>
                      </div>
                    )}

                    {/* Diff */}
                    {item.evidence.diff && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1.5">Changes</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-red-500/5 rounded-lg p-3 border border-red-500/10">
                            <p className="text-[10px] font-semibold text-red-600 dark:text-red-400 mb-1">Before</p>
                            <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{item.evidence.diff.before}</pre>
                          </div>
                          <div className="bg-emerald-500/5 rounded-lg p-3 border border-emerald-500/10">
                            <p className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 mb-1">After</p>
                            <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{item.evidence.diff.after}</pre>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Related records */}
                    {item.evidence.relatedRecords && item.evidence.relatedRecords.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1.5">Context</p>
                        <div className="space-y-1">
                          {item.evidence.relatedRecords.map((r, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span className="text-muted-foreground min-w-[120px]">{r.label}:</span>
                              <span>{r.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Risk factors */}
                    {item.evidence.riskFactors && item.evidence.riskFactors.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1.5">Risk Factors</p>
                        <div className="flex flex-wrap gap-1.5">
                          {item.evidence.riskFactors.map((f, i) => (
                            <Badge key={i} variant="outline" className="text-[9px] bg-amber-500/5 text-amber-600 dark:text-amber-400 border-amber-500/20">
                              <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> {f}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t ml-7">
                  <Button size="sm" className="gap-1.5" onClick={() => handleApprove(item.id)}>
                    <CheckCircle className="h-3.5 w-3.5" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => navigate({ to: '/approvals/$approvalId', params: { approvalId: item.id } })}>
                    Edit & Approve
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 text-red-600 dark:text-red-400 border-red-500/30 hover:bg-red-500/10" onClick={() => { setRejectTarget(item); setRejectReason(''); }}>
                    <XCircle className="h-3.5 w-3.5" /> Reject
                  </Button>
                  <Button size="sm" variant="ghost" className="gap-1.5 ml-auto" onClick={() => navigate({ to: '/approvals/$approvalId', params: { approvalId: item.id } })}>
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {filtered.length === 0 && (
          <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center">
            <CheckCircle className="h-8 w-8 text-emerald-500/30 mb-2" />
            <p className="text-sm font-medium text-muted-foreground">All caught up!</p>
            <p className="text-xs text-muted-foreground/70 mt-1">No pending approvals matching your filters</p>
          </div>
        )}
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={open => { if (!open) setRejectTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject: {rejectTarget?.action.title}</DialogTitle>
            <DialogDescription>Provide a reason for rejecting this action. The requesting agent and user will be notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Reason<span className="text-red-500 ml-0.5">*</span></Label>
              <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={3} className="text-xs" placeholder="Why are you rejecting this action?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!rejectReason.trim()} onClick={handleReject}>
              <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk approve confirmation */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Approve {bulkApprovable.length} Items</DialogTitle>
            <DialogDescription>
              Only low-risk items with single-confirm level can be bulk approved. The following items will be approved:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[200px] overflow-auto">
            {bulkApprovable.map(item => (
              <div key={item.id} className="flex items-center gap-2 text-xs p-2 bg-muted/30 rounded">
                <AgentIcon iconName={item.agent.icon} colorName={item.agent.iconColor} size="sm" />
                <span className="flex-1 truncate">{item.action.title}</span>
                <Badge variant="outline" className="text-[9px]">Risk: {Math.round(item.riskScore)}%</Badge>
              </div>
            ))}
          </div>
          {hasNonBulkApprovable && (
            <p className="text-[10px] text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3 inline mr-1" />
              {selectedItems.length - bulkApprovable.length} selected item(s) skipped: high risk (&gt;50%) or requires supervisor/2-person approval
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkApprove} className="gap-1.5">
              <CheckCircle className="h-3.5 w-3.5" /> Approve {bulkApprovable.length} Items
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
